import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { initializeAuth, logout, refreshProfile } from "@/src/auth/store/auth.thunks";
import type { AuthSession, AuthState, ProfileRow } from "@/src/auth/types/auth.types";

const initialState: AuthState = {
	session: null,
	profile: null,
	schoolIds: [],
	isLoading: false,
	isInitialized: false,
	error: null,
};

export const authSlice = createSlice({
	name: "auth",
	initialState,
	reducers: {
		setSession(state, action: PayloadAction<AuthSession | null>) {
			state.session = action.payload;
			if (!action.payload) {
				state.profile = null;
				state.schoolIds = [];
			}
		},
		setProfile(state, action: PayloadAction<{ profile: ProfileRow | null; schoolIds: string[] }>) {
			state.profile = action.payload.profile;
			state.schoolIds = action.payload.schoolIds;
		},
		clearError(state) {
			state.error = null;
		},
		resetAuth() {
			return { ...initialState, isInitialized: true };
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(initializeAuth.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(initializeAuth.fulfilled, (state, action) => {
				state.isLoading = false;
				state.isInitialized = true;
				state.session = action.payload.session;
				state.profile = action.payload.profile;
				state.schoolIds = action.payload.schoolIds;
			})
			.addCase(initializeAuth.rejected, (state, action) => {
				state.isLoading = false;
				state.isInitialized = true;
				state.error = action.error.message ?? "Failed to initialize session";
			})
			.addCase(refreshProfile.fulfilled, (state, action) => {
				state.profile = action.payload.profile;
				state.schoolIds = action.payload.schoolIds;
			})
			.addCase(logout.fulfilled, () => {
				return { ...initialState, isInitialized: true };
			});
	},
});

export const { setSession, setProfile, clearError, resetAuth } = authSlice.actions;
export const authReducer = authSlice.reducer;
